/**
 * Value classification — the org-level vocabulary that makes value-driven
 * payoff/charge-off methods REAL. Generalizes the original status-only mechanism
 * to ALL classifiable fields: status fields (loan_status / closure_reason /
 * charge_off_status) AND flag fields (charge_off_flag / payoff_flag /
 * prepayment_flag).
 *
 * Every bank's core system emits different tokens ("Closed", "Paid in Full",
 * "CO", "PIF", "Y", "1", …), so the product never guesses what a value means.
 * When a column is mapped to one of the VALUE-CLASSIFIABLE Standard fields, the
 * mapping step shows the column's distinct values and the customer classifies
 * each one against that field's FAMILY vocabulary:
 *
 *   - status family → `paid_off` / `charged_off` / `active`.
 *   - flag family   → `event` / `not_event`.
 *
 * The classifications are DURABLE and org-scoped (like the field registry): a
 * re-upload with the same vocabulary needs no re-work, and only NEW values ask
 * for review. The modeling derive folds a `paid_off`/`event`-on-payoff value
 * into the payoff flag and a `charged_off`/`event`-on-charge-off value into the
 * charge-off flag — so a loan that is "ready via a status/flag column" actually
 * contributes events to the curves.
 *
 * Values are matched NORMALIZED (lowercased, trimmed) so `"Paid Off "` and
 * `"paid off"` classify once.
 */

import type { StandardFieldKey } from './catalog';

/** The Standard fields whose values carry a classifiable STATUS vocabulary. */
export const STATUS_CLASSIFIABLE_FIELD_KEYS = [
  'loan_status',
  'closure_reason',
  'charge_off_status',
] as const satisfies readonly StandardFieldKey[];
export type StatusClassifiableFieldKey = (typeof STATUS_CLASSIFIABLE_FIELD_KEYS)[number];

/** The Standard fields whose values carry a classifiable FLAG vocabulary. */
export const FLAG_CLASSIFIABLE_FIELD_KEYS = [
  'charge_off_flag',
  'payoff_flag',
  'prepayment_flag',
] as const satisfies readonly StandardFieldKey[];
export type FlagClassifiableFieldKey = (typeof FLAG_CLASSIFIABLE_FIELD_KEYS)[number];

/** Every Standard field whose values are classifiable (status ∪ flag). */
export const VALUE_CLASSIFIABLE_FIELD_KEYS = [
  ...STATUS_CLASSIFIABLE_FIELD_KEYS,
  ...FLAG_CLASSIFIABLE_FIELD_KEYS,
] as const;
export type ValueClassifiableFieldKey = (typeof VALUE_CLASSIFIABLE_FIELD_KEYS)[number];

/** The three classifications a STATUS value can carry. */
export const STATUS_CLASSIFICATIONS = ['paid_off', 'charged_off', 'active'] as const;
export type StatusClassification = (typeof STATUS_CLASSIFICATIONS)[number];

/** The two classifications a FLAG value can carry. */
export const FLAG_CLASSIFICATIONS = ['event', 'not_event'] as const;
export type FlagClassification = (typeof FLAG_CLASSIFICATIONS)[number];

/** Any classification value, across both family vocabularies. */
export const VALUE_CLASSIFICATIONS = [
  ...STATUS_CLASSIFICATIONS,
  ...FLAG_CLASSIFICATIONS,
] as const;
export type ValueClassification = (typeof VALUE_CLASSIFICATIONS)[number];

/** Which family vocabulary a classifiable field key draws from. */
export type ClassificationFamily = 'status' | 'flag';

/**
 * The family a classifiable field key belongs to — `flag` for the flag fields,
 * `status` for everything else (the status fields; also the safe default for a
 * non-classifiable key, which callers never reach the vocabulary for anyway).
 */
export function classificationFamily(
  key: StandardFieldKey | string | null,
): ClassificationFamily {
  return key !== null && (FLAG_CLASSIFIABLE_FIELD_KEYS as readonly string[]).includes(key)
    ? 'flag'
    : 'status';
}

/** The family vocabulary a classifiable field's values are chosen from. */
export function classificationOptionsForField(
  key: StandardFieldKey | string | null,
): readonly string[] {
  return classificationFamily(key) === 'flag' ? FLAG_CLASSIFICATIONS : STATUS_CLASSIFICATIONS;
}

/** Whether a Standard-field key is STATUS-classifiable. */
export function isStatusClassifiableFieldKey(
  key: StandardFieldKey | string | null,
): key is StatusClassifiableFieldKey {
  return (
    key !== null && (STATUS_CLASSIFIABLE_FIELD_KEYS as readonly string[]).includes(key)
  );
}

/** Whether a Standard-field key is FLAG-classifiable. */
export function isFlagClassifiableFieldKey(
  key: StandardFieldKey | string | null,
): key is FlagClassifiableFieldKey {
  return key !== null && (FLAG_CLASSIFIABLE_FIELD_KEYS as readonly string[]).includes(key);
}

/** Whether a Standard-field key is value-classifiable (status ∪ flag). */
export function isValueClassifiableFieldKey(
  key: StandardFieldKey | string | null,
): key is ValueClassifiableFieldKey {
  return (
    key !== null && (VALUE_CLASSIFIABLE_FIELD_KEYS as readonly string[]).includes(key)
  );
}

/**
 * Normalize a classifiable value for classification identity: lowercase + trim.
 * The SAME normalization runs in the derive SQL (`lower(btrim(...))`) — keep them
 * in lockstep or a classified value will silently stop matching.
 */
export function normalizeClassifiableValue(value: string): string {
  return value.trim().toLowerCase();
}

/**
 * Cap on the distinct values a classifiable column may carry. A genuine status /
 * flag column has a handful of tokens; hundreds means the column is mis-mapped
 * (an id or free-text column), so the mapping step blocks with a "too many
 * distinct values" error instead of rendering an unusable classification list.
 */
export const CLASSIFIABLE_VALUES_MAX = 50;

// ── WS-H (F170): event-marking vocabulary — the readiness gate ───────────────

/**
 * Per READINESS-CLAUSE key, the classification(s) that make the key a LIVE event
 * source — the exact predicate the derive's `pivotSelectColumns` payoff /
 * charge-off fold applies (cohort-facts-sql.ts): a `loan_status`/`closure_reason`
 * value folds a payoff only when classified `paid_off`; a flag value only when
 * classified `event`; a `charge_off_status` value marks a charge-off only when
 * classified `charged_off`. Keys are scoped to how READINESS consumes them
 * (loan_status/closure_reason appear only in payoff clauses, so only `paid_off`
 * unlocks them there — a `charged_off`-only status vocabulary feeds the derive's
 * CO fold but unlocks no readiness clause).
 *
 * Readiness rule (F170): a clause key listed here satisfies NOTHING — not by the
 * loan's own presence, not by the portfolio-level signal — unless the org's value
 * vocabulary carries ≥1 value classified with one of the listed classifications
 * for that field. An all-`active` status book or an unclassified `Y`/`1` flag
 * column derives no events, so it must not read "ready" (CPR≈0 under a confident
 * headline was the F170 failure). Pre-accept this is the strictly-conservative
 * posture: unclassified = not-yet-event (honest under-count, classify-to-unlock).
 */
export const EVENT_MARKING_CLASSIFICATIONS = {
  loan_status: ['paid_off'],
  closure_reason: ['paid_off'],
  charge_off_status: ['charged_off'],
  charge_off_flag: ['event'],
  payoff_flag: ['event'],
  prepayment_flag: ['event'],
} as const satisfies Partial<Record<StandardFieldKey, readonly ValueClassification[]>>;

/** The clause keys that require a live event vocabulary (F170) — derived. */
export const EVENT_VOCABULARY_FIELD_KEYS = Object.keys(
  EVENT_MARKING_CLASSIFICATIONS,
) as readonly (keyof typeof EVENT_MARKING_CLASSIFICATIONS)[];

const EVENT_VOCABULARY_KEY_SET: ReadonlySet<string> = new Set(EVENT_VOCABULARY_FIELD_KEYS);

/** Whether a readiness clause key requires a live event vocabulary (F170). */
export function requiresEventVocabulary(key: StandardFieldKey): boolean {
  return EVENT_VOCABULARY_KEY_SET.has(key);
}

/**
 * Whether one org-wide classification row makes `key` vocabulary-live: the
 * (fieldKey, classification) pair matches {@link EVENT_MARKING_CLASSIFICATIONS}.
 * The server helper (`dbOrgLiveEventVocabulary`) and the in-memory Review oracle
 * both fold rows through this ONE predicate.
 */
export function isEventMarkingClassification(
  fieldKey: string,
  classification: string,
): boolean {
  const marking = (EVENT_MARKING_CLASSIFICATIONS as Record<string, readonly string[] | undefined>)[
    fieldKey
  ];
  return marking?.includes(classification) ?? false;
}
